/** Client-side half of the mapping-policy contract.
 *
 *  Two questions the product table has to answer per row, both of which must
 *  re-answer instantly when the default policy changes — so they are computed
 *  here from data already in hand rather than re-fetched:
 *
 *    1. what would this policy MINT for this product?
 *    2. does a mapping already exist for that id?
 *
 *  Minting is derived from the policy's `form` (`{product}`, `{option}`,
 *  `{product}/{HH:MM}`) rather than from a hand-written switch, so the client
 *  cannot drift from `demo/lib/integrations/mapping_policies.py`. A form whose
 *  placeholders the catalogue row cannot fill yields nothing, which is the same
 *  answer the backend gives and the reason `mintable` exists: a qualified form
 *  means one tour per departure time, and the listing carries no times.
 */
import type { MappingPolicy, ProductListItem, ProductMapping } from '@/api/sync';

export interface MintedMapping {
  /** The external ID this policy gives the product. */
  value: string;
  /** Set when the id came from one of the product's options. */
  optionId: string | null;
}

/** Placeholders a form needs, in order of appearance. */
export function placeholders(form: string): string[] {
  return [...form.matchAll(/\{([^}]+)\}/g)].map((m) => m[1] ?? '');
}

/** OCTO's id for a product's only/default option: product-scoped, shared by most
 *  single-option products, and skipped by the booking matcher (mirrors
 *  `mapping_policies.OCTO_DEFAULT_OPTION_ID`). */
export const OCTO_DEFAULT_OPTION_ID = 'DEFAULT';

/** Everything `policy` would create for `product`, or [] if it cannot.
 *
 *  A list because a policy can fan a product out: `{option}` makes one mapping
 *  per option. Empty means "the catalogue row has no source for a placeholder
 *  in this form" — never "nothing to do".
 */
export function mintForProduct(policy: MappingPolicy, product: ProductListItem): MintedMapping[] {
  if (!product.id) return [];
  if (policy.templated) {
    // Rendered server-side at fetch time, against the RAW product payload --
    // the normalised row this component sees drops every field worth
    // templating on, so the client cannot compute it.
    return product.template_id ? [{ value: product.template_id, optionId: null }] : [];
  }
  const needed = placeholders(policy.form);
  // `product` and `option` are the only placeholders a catalogue row can fill.
  // Departure times and ticket types are not in the listing at all.
  if (needed.some((p) => p !== 'product' && p !== 'option')) return [];

  const fill = (optionId: string | null) =>
    policy.form.replace('{product}', product.id ?? '').replace('{option}', optionId ?? '');

  if (needed.includes('option')) {
    const out: MintedMapping[] = [];
    for (const c of product.children) {
      if (c.id === OCTO_DEFAULT_OPTION_ID) {
        // Bookings of OCTO's shared default option match the bare product id (the matcher
        // skips the option forms for it), so mint the product id -- never the literal.
        if (!out.some((m) => m.optionId === null)) out.push({ value: product.id, optionId: null });
        continue;
      }
      out.push({ value: fill(c.id), optionId: c.id });
    }
    return out;
  }
  return [{ value: fill(null), optionId: null }];
}

export type MatchState =
  /** Every id this policy would mint already exists. */
  | 'mapped'
  /** Some but not all — a product whose options were partly mapped. */
  | 'partial'
  /** None of them exist yet. */
  | 'unmapped'
  /** The policy cannot be created from a catalogue row at all. */
  | 'not-mintable'
  /** The policy would mint nothing: an option policy on an option-less product. */
  | 'nothing-to-mint';

export interface PolicyStatus {
  state: MatchState;
  /** Ids this policy would create, for the "would mint" column. */
  minted: MintedMapping[];
  /** Of those, the ones that already exist. */
  existing: ProductMapping[];
  /** Policies this product is ALREADY mapped under, whatever is selected —
   *  this is what makes a conflict visible before the operator syncs. */
  currentPolicies: string[];
  /** True when at least one existing mapping is under a different policy. */
  conflicting: boolean;
  /** The additional IDs (`via: "ext_id"`) of OTHER tours that hold a value this
   *  policy mints for the product. Non-empty means the product's tour key
   *  resolves to another tour, so a sync leaves the product out instead of
   *  overwriting that tour with its content (the re-sync guard) unless it is
   *  split off. Mirrors the importer's alias lookup: a parked row
   *  (`use_for_matching: false`) resolves nothing, and an alias on the tour
   *  whose own ID is that same value is that tour, not another one. */
  elsewhere: ProductMapping[];
}

/** The rows of `present` that make a minted value resolve to another tour. */
function aliasesElsewhere(present: ProductMapping[], wanted: Set<string>): ProductMapping[] {
  return present.filter(
    (m) =>
      m.via === 'ext_id' &&
      wanted.has(m.custom_id) &&
      m.use_for_matching !== false &&
      !present.some(
        (p) => p.via === 'custom_id' && p.custom_id === m.custom_id && p.tour.id === m.tour.id,
      ),
  );
}

/** How `product` stands against `policy`, given the mappings it already has. */
export function policyStatus(
  policy: MappingPolicy | undefined,
  product: ProductListItem,
  mappings: ProductMapping[] | undefined,
): PolicyStatus {
  const present = mappings ?? [];
  const currentPolicies = [
    ...new Set(present.map((m) => m.policy.policy).filter((p): p is string => Boolean(p))),
  ];

  if (!policy || !policy.mintable) {
    return {
      state: 'not-mintable',
      minted: [],
      existing: [],
      currentPolicies,
      conflicting: false,
      elsewhere: [],
    };
  }

  const minted = mintForProduct(policy, product);
  if (minted.length === 0) {
    return {
      state: 'nothing-to-mint',
      minted,
      existing: [],
      currentPolicies,
      conflicting: false,
      elsewhere: [],
    };
  }

  const wanted = new Set(minted.map((m) => m.value));
  const existing = present.filter((m) => wanted.has(m.custom_id));
  const state: MatchState =
    existing.length === 0 ? 'unmapped' : existing.length >= wanted.size ? 'mapped' : 'partial';

  return {
    state,
    minted,
    existing,
    currentPolicies,
    // A product mapped under something else is exactly the case the pre-flight
    // warns about: syncing under the new policy leaves the old mapping live.
    conflicting: currentPolicies.some((p) => p !== policy.id),
    elsewhere: aliasesElsewhere(present, wanted),
  };
}

/** The mappings belonging to one option of a product.
 *
 *  The API keys mappings by the CATALOGUE PRODUCT, not by the value, so a child
 *  row finds its own by the classification's `option_id` rather than by
 *  looking itself up — an option id is not a key in that map.
 */
export function mappingsForOption(
  mappings: ProductMapping[] | undefined,
  optionId: string,
): ProductMapping[] {
  return (mappings ?? []).filter((m) => m.policy.option_id === optionId);
}

/** Short operator-facing label for a match state. */
export function stateLabel(state: MatchState): string {
  switch (state) {
    case 'mapped':
      return 'mapped';
    case 'partial':
      return 'partly mapped';
    case 'unmapped':
      return 'unmapped';
    case 'nothing-to-mint':
      return 'no options';
    case 'not-mintable':
      return 'not creatable';
  }
}

export function stateColor(state: MatchState): 'success' | 'warning' | 'default' {
  switch (state) {
    case 'mapped':
      return 'success';
    case 'unmapped':
    case 'partial':
      return 'warning';
    default:
      return 'default';
  }
}
